<script setup>
import { ref, watch, onMounted } from 'vue';
import getNavigationIcon from '@services/GetNavaigationIcon';
import BackwardIcon from '@assets/img/navigation_icons/backward-icon.png';
const props = defineProps({
    showPartialHistory: {
        type: Boolean,
        default: false,
    },
    historyType: {
        type: String,
        default: 'forward', // 'forward' or 'previous'
    }
});

const emit = defineEmits(['close', 'back']);

let openPartialHistory = ref(false);
const historyItems = ref([]);

const navigateToHistoryItem = (item) => {
    console.log("Navigating to:", item);
    openPartialHistory.value = false;
    emit('close');
};

const closePartialHistory = () => {
    openPartialHistory.value = false;
    emit('close');
};

const handleBack = () => {
    openPartialHistory.value = false;
    emit('back');
};

const loadHistory = () => {
    const storageKey = props.historyType === 'forward' ? 'forward-toggle-state' : 'back-toggle-state';
    let history = localStorage.getItem(storageKey);
    let historyArray = history ? JSON.parse(history) : [];
    historyItems.value = historyArray.reverse();
};

watch(() => props.showPartialHistory, (newVal) => {
    console.log("partial history watch", newVal);
    if (newVal) {
        loadHistory();
    }
    openPartialHistory.value = newVal;
});

watch(() => props.historyType, () => {
    if (openPartialHistory.value) {
        loadHistory();
    }
});

onMounted(() => {
    loadHistory();
});
</script>

<template>
    <!-- Partial History Overlay (click outside to close) -->
    <div v-if="openPartialHistory" class="partial-history-overlay" @click="closePartialHistory"></div>
    
    <!-- Partial History Dropdown -->
    <div v-if="openPartialHistory" class="partial-history-dropdown">
        <div class="partial-history-section">
            <div class="partial-history-header">
                <span class="partial-history-title">
                    {{ historyType === 'forward' ? 'Forward Pages' : 'Previous Pages' }}
                </span>
                <span class="back-button" @click="handleBack" aria-label="Back" title="Back">
                    <img :src="BackwardIcon" alt="Back" width="20" height="20"> Back
                </span>
            </div>
            <div class="partial-history-items">
                <div
                    v-for="item in historyItems"
                    :key="item.id"
                    class="partial-history-item"
                    @click="navigateToHistoryItem(item)"
                >
                    <div class="partial-history-icon">
                        <img :src="getNavigationIcon.getIcon(item.icon)" alt="icon" width="24" height="24">
                    </div>
                    <router-link :to="item.url" class="partial-history-link">
                        <span class="partial-history-title" :title="item.title">
                            {{ item.title }}
                        </span>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.partial-history-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    background: var(--bg-primary);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    min-width: 280px;
    max-width: 350px; /* Added max-width to force truncation */
    z-index: calc(var(--z-dropdown) + 1);
    margin-top: var(--spacing-sm);
    padding: var(--spacing-lg);
    max-height: 70vh;
    display: flex;
    flex-direction: column;
}

.partial-history-section {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden; /* Ensure content stays within */
}

.partial-history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-sm);
    flex-shrink: 0;
}

.back-button {
    font-size: 12px;
   font-weight: 400;
   color:#412CC9;
   cursor: pointer;
   user-select: none;
}

.back-button:hover {
    background: var(--bg-tertiary);
}

.back-button img {
    opacity: 0.8;
    transition: opacity 0.2s;
}

.back-button:hover img {
    opacity: 1;
}

.partial-history-title {
    font-size: 12px;
   font-weight: 400;
   color:#101828
}

.partial-history-items {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    max-height: calc(70vh - 100px);
}

.partial-history-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: var(--spacing-sm);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background-color var(--transition-normal);
}

.partial-history-item:hover {
    background: var(--bg-tertiary);
}

.partial-history-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background-color:#F7F5FF;
    border-radius: 50%;
}

/* Updated selector to use class instead of tag name since router-link renders as 'a' */
.partial-history-link {
    flex: 1;
    text-decoration: none;
    color: inherit;
    overflow: hidden;
    min-width: 0; /* Critical for flex child truncation */
    display: flex; /* alignment */
    align-items: center;
}

.partial-history-item .partial-history-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%; /* Take full width of parent */
    display: block;
    color: var(--text-primary);
    font-size: var(--font-size-xs);
}

@media (max-width: 768px) {
    .partial-history-dropdown {
        position: fixed;
        top: 120px;
        left: 16px;
        right: 16px;
        width: auto;
        max-width: none; /* Reset max-width on mobile if needed or keep it */
        margin-top: 0;
        max-height: 60vh;
        overflow-y: auto;
    }
}

@media (max-width: 480px) {
    .partial-history-dropdown {
        top: 100px;
        left: 8px;
        right: 8px;
    }
}

.partial-history-overlay {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   z-index: var(--z-dropdown);
   background: transparent;
}
</style>
